import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { CheckCircle, TrendingUp, Award, Shield, BarChart3, Leaf, ArrowRight, Clock } from 'lucide-react';

const colorMap = {
  emerald: { ring: 'ring-emerald-200', bg: 'from-emerald-50 to-emerald-100', badge: 'bg-emerald-600', text: 'text-emerald-700', btn: 'bg-emerald-600 hover:bg-emerald-700' },
  blue: { ring: 'ring-blue-200', bg: 'from-blue-50 to-blue-100', badge: 'bg-blue-600', text: 'text-blue-700', btn: 'bg-blue-600 hover:bg-blue-700' },
  purple: { ring: 'ring-purple-200', bg: 'from-purple-50 to-purple-100', badge: 'bg-purple-600', text: 'text-purple-700', btn: 'bg-purple-600 hover:bg-purple-700' },
  amber: { ring: 'ring-amber-200', bg: 'from-amber-50 to-amber-100', badge: 'bg-amber-600', text: 'text-amber-700', btn: 'bg-amber-600 hover:bg-amber-700' },
  rose: { ring: 'ring-rose-200', bg: 'from-rose-50 to-rose-100', badge: 'bg-rose-600', text: 'text-rose-700', btn: 'bg-rose-600 hover:bg-rose-700' },
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [cms, setCms] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(true);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site-settings`).then(r => r.json()).then(setCms).catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client/assessments`)
      .then(r => r.json())
      .then(data => { setAssessments(Array.isArray(data) ? data.filter(a => a.is_active !== false) : []); setAssessmentsLoading(false); })
      .catch(() => setAssessmentsLoading(false));
  }, []);

  const heroTitle = cms?.landing_hero_title || 'Sustainability & Compliance Assessments';
  const heroSubtitle = cms?.landing_hero_subtitle || 'Measure your readiness against EU regulations — ESRS, DORA and more.';

  const scrollToAssessments = () => {
    document.getElementById('assessments-grid')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-600 via-emerald-700 to-blue-800 text-white">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fade-in-up" data-testid="hero-title">
              {heroTitle}
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-emerald-100 max-w-3xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.1s' }} data-testid="hero-subtitle">
              {heroSubtitle}
            </p>
            <button
              onClick={scrollToAssessments}
              data-testid="start-assessment-btn"
              className="bg-white text-emerald-700 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-emerald-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 animate-fade-in-up"
              style={{ animationDelay: '0.2s' }}
            >
              Choose Your Assessment
            </button>
          </div>
        </div>
      </section>

      {/* Available Assessments Grid */}
      <section id="assessments-grid" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4 gradient-text">Available Assessments</h2>
          <p className="text-center text-gray-600 mb-12 text-lg">
            Start any assessment for free — pay only when you unlock the full report.
          </p>
          {assessmentsLoading ? (
            <div className="text-center py-16 text-gray-400" data-testid="assessments-loading">Loading assessments...</div>
          ) : assessments.length === 0 ? (
            <div className="text-center py-16 text-gray-500" data-testid="no-assessments">
              No assessments configured yet. An administrator can create one from the admin portal.
            </div>
          ) : (
            <div className={`grid gap-6 ${assessments.length === 1 ? 'md:grid-cols-1 max-w-xl mx-auto' : assessments.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
              {assessments.map(a => {
                const palette = colorMap[a.color] || colorMap.emerald;
                const Icon = Icons[a.icon] || Leaf;
                const price = a.price_eur != null ? `€${Number(a.price_eur).toLocaleString()}` : null;
                return (
                  <div key={a.id} data-testid={`assessment-card-${a.id}`}
                    className={`bg-gradient-to-br ${palette.bg} rounded-2xl p-7 shadow-sm hover:shadow-xl transition-all duration-300 ring-1 ${palette.ring} flex flex-col`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className={`${palette.badge} w-14 h-14 rounded-xl flex items-center justify-center shadow-md`}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      {a.duration_estimate && (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-white/70 border border-white rounded-full px-2.5 py-1">
                          <Clock className="w-3 h-3" /> {a.duration_estimate}
                        </span>
                      )}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{a.name}</h3>
                    <p className="text-sm text-gray-700 mb-4 leading-relaxed flex-1">
                      {a.landing_description || a.description || '—'}
                    </p>
                    {Array.isArray(a.landing_features) && a.landing_features.length > 0 && (
                      <ul className="space-y-2 mb-5">
                        {a.landing_features.slice(0, 5).map((feat, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${palette.text}`} />
                            <span>{feat}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="flex items-center justify-between pt-4 border-t border-white">
                      <div>
                        {price && (
                          <>
                            <span className={`text-2xl font-bold ${palette.text}`}>{price}</span>
                            <span className="text-xs text-gray-500 ml-1">/ report</span>
                          </>
                        )}
                      </div>
                      <button onClick={() => navigate(`/assessment/${a.id}`)}
                        data-testid={`start-assessment-${a.id}`}
                        className={`inline-flex items-center gap-2 ${palette.btn} text-white font-semibold px-5 py-2.5 rounded-lg transition-colors`}>
                        Start <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us (CMS-editable) */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4 gradient-text">
            {cms?.why_title || 'Why Choose Our Platform?'}
          </h2>
          {cms?.why_subtitle && (
            <p className="text-center text-gray-600 text-lg mb-16 max-w-2xl mx-auto">{cms.why_subtitle}</p>
          )}
          {(() => {
            const defaultFeatures = [
              { title: 'Standards-aligned', description: 'Every assessment is designed against recognised EU frameworks — ESRS, DORA, and more.', icon: 'Shield', color: 'emerald' },
              { title: 'Expert guidance', description: 'Clear, actionable recommendations for every answer, with an optional expert review.', icon: 'TrendingUp', color: 'blue' },
              { title: 'Professional reports', description: 'Download branded PDF reports you can share with your board, auditors and investors.', icon: 'Award', color: 'purple' },
            ];
            const features = (Array.isArray(cms?.why_features) && cms.why_features.length > 0) ? cms.why_features : defaultFeatures;
            const bgMap = {
              emerald: 'from-emerald-50 to-emerald-100', blue: 'from-blue-50 to-blue-100',
              purple: 'from-purple-50 to-purple-100', amber: 'from-amber-50 to-amber-100', rose: 'from-rose-50 to-rose-100',
            };
            const badgeMap = {
              emerald: 'bg-emerald-600', blue: 'bg-blue-600',
              purple: 'bg-purple-600', amber: 'bg-amber-600', rose: 'bg-rose-600',
            };
            const textMap = {
              emerald: 'text-emerald-900', blue: 'text-blue-900',
              purple: 'text-purple-900', amber: 'text-amber-900', rose: 'text-rose-900',
            };
            return (
              <div className={`grid gap-8 ${features.length === 2 ? 'md:grid-cols-2 max-w-4xl mx-auto' : 'md:grid-cols-3'}`}>
                {features.map((f, i) => {
                  const Icon = Icons[f.icon] || Shield;
                  const c = f.color || 'emerald';
                  return (
                    <div key={i} className={`p-8 rounded-xl bg-gradient-to-br ${bgMap[c] || bgMap.emerald} card-hover`} data-testid={`feature-card-${i}`}>
                      <div className={`${badgeMap[c] || badgeMap.emerald} w-16 h-16 rounded-full flex items-center justify-center mb-6`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className={`text-2xl font-bold mb-4 ${textMap[c] || textMap.emerald}`}>{f.title}</h3>
                      <p className="text-gray-700">{f.description}</p>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4 gradient-text">
            Flexible Pricing Options
          </h2>
          <p className="text-center text-gray-600 mb-16 text-lg">
            Choose the plan that fits your needs
          </p>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { name: 'Single Report', price: cms?.pricing_settings?.single_report_eur || 50, color: 'emerald', features: ['One-time assessment', 'Complete ESG evaluation', 'Detailed recommendations'] },
              { name: '1,000 Reports/mo', price: cms?.pricing_settings?.basic_plan_eur || 1000, color: 'emerald', features: ['1,000 assessments', 'Invoice billing option', 'Priority support'] },
              { name: '3,000 Reports/mo', price: cms?.pricing_settings?.professional_plan_eur || 2500, color: 'blue', popular: true, features: ['3,000 assessments', 'Invoice billing', 'Dedicated support', 'Custom branding'] },
              { name: '5,000 Reports/mo', price: cms?.pricing_settings?.enterprise_plan_eur || 4000, color: 'purple', enterprise: true, features: ['5,000 assessments', 'Invoice billing', '24/7 Premium support', 'API access'] },
            ].map((plan, i) => (
              <div key={i} className={`bg-gradient-to-br ${plan.color === 'blue' ? 'from-blue-50 to-blue-100 border-blue-400' : plan.color === 'purple' ? 'from-purple-50 to-purple-100 border-purple-400' : 'from-gray-50 to-white border-gray-200'} p-8 rounded-xl shadow-lg border-2 card-hover ${plan.popular ? 'relative' : ''}`}>
                {plan.popular && <div className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold inline-block mb-4">Popular</div>}
                {plan.enterprise && <div className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-semibold inline-block mb-4">Enterprise</div>}
                <h3 className="text-2xl font-bold mb-2 text-gray-900">{plan.name}</h3>
                <div className="mb-6">
                  <span className={`text-4xl font-bold ${plan.color === 'blue' ? 'text-blue-600' : plan.color === 'purple' ? 'text-purple-600' : 'text-emerald-600'}`}>
                    €{plan.price.toLocaleString()}
                  </span>
                  {i > 0 && <span className="text-gray-600 ml-2 block text-sm">/month</span>}
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f, fi) => (
                    <li key={fi} className="flex items-start">
                      <CheckCircle className={`w-5 h-5 ${plan.color === 'blue' ? 'text-blue-600' : plan.color === 'purple' ? 'text-purple-600' : 'text-emerald-600'} mr-2 mt-0.5 flex-shrink-0`} />
                      <span className="text-gray-700">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            {cms?.cta_title || 'Ready to find out where you stand?'}
          </h2>
          <p className="text-xl mb-8 text-emerald-100">
            {cms?.cta_subtitle || 'Start any assessment for free — pay only to unlock the full report.'}
          </p>
          <button
            onClick={scrollToAssessments}
            data-testid="cta-start-btn"
            className="bg-white text-emerald-700 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-emerald-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105"
          >
            Browse Assessments
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-white text-lg font-bold mb-4">About</h3>
              <p className="text-sm">
                {cms?.footer_about || 'Professional assessment platform for EU compliance and sustainability reporting.'}
              </p>
            </div>
            <div>
              <h3 className="text-white text-lg font-bold mb-4">Standards</h3>
              <ul className="text-sm space-y-2">
                <li>• ESRS Compliance</li>
                <li>• GHG Protocol</li>
                <li>• ISO Standards</li>
                <li>• CSRD Requirements</li>
              </ul>
            </div>
            <div>
              <h3 className="text-white text-lg font-bold mb-4">Contact</h3>
              <p className="text-sm">
                For support or inquiries about enterprise plans, please contact our team.
              </p>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-sm">
            <p>© 2025 Sustainability Scoring Platform. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
